import styled from 'styled-components';
import { MessageSquarePlus } from 'lucide-react';
import { useSupportWidgetStore } from '../../../../stores/supportWidgetStore';
import { useMySupportRequests } from '../../../../hooks/useSupport';
import { RequestCard } from '../RequestCard';
import { CenteredState } from '../supportShared';
import { LoadingSpinner } from '../../../ui/LoadingSpinner';

const Pad = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const NewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1.5px dashed ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.light};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LoadMoreButton = styled.button`
  align-self: center;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.primary.base};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export function MessagesTab() {
  const goToCompose = useSupportWidgetStore((s) => s.goToCompose);
  const openThread = useSupportWidgetStore((s) => s.openThread);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useMySupportRequests();
  const requests = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <Pad>
      <NewButton type="button" onClick={() => goToCompose()}>
        <MessageSquarePlus size={16} /> Send us a message
      </NewButton>

      {isLoading && (
        <CenteredState>
          <LoadingSpinner size="md" />
        </CenteredState>
      )}

      {!isLoading && requests.length === 0 && (
        <EmptyState>No messages yet — send us one and we&apos;ll reply here.</EmptyState>
      )}

      {requests.map((r) => (
        <RequestCard key={r.id} request={r} onClick={() => openThread(r.id)} />
      ))}

      {hasNextPage && (
        <LoadMoreButton type="button" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </LoadMoreButton>
      )}
    </Pad>
  );
}

export default MessagesTab;
